// BlogPage.jsx

import React, {
  useMemo,
  useState
} from "react";

import {
  Link
} from "react-router-dom";

import MarketingPageShell from "../components/layout/MarketingPageShell";
import SeoHead from "../components/seo/SeoHead";

import StructuredData, {
  buildWebPageSchema,
  buildOrganizationSchema
} from "../components/seo/StructuredData";

import useThemeMode from "../hooks/useThemeMode";
import { getMarketingTheme } from "../utils/marketingTheme";

import useI18n from "../i18n/useI18n";

import blogPosts from "../data/blogPosts";

function BlogPage() {
  const { isDark } =
    useThemeMode();

  const colors = useMemo(
    () => getMarketingTheme(isDark),
    [isDark]
  );

  const { t } = useI18n();

  const [
    searchQuery,
    setSearchQuery
  ] = useState("");

  const [
    activeCategory,
    setActiveCategory
  ] = useState("all");

  const tt = (
    key,
    fallback
  ) => {
    const value = t(key);

    return value === key ||
      value === undefined ||
      value === null
      ? fallback
      : value;
  };

  const categories = [
    {
      label: "All",
      slug: "all"
    },

    {
      label: "Hooks",
      slug: "hooks"
    },

    {
      label: "AI Tools",
      slug: "ai-tools"
    },

    {
      label:
        "Creator Workflow",
      slug:
        "creator-workflow"
    },

    {
      label: "Retention",
      slug: "retention"
    },

    {
      label: "Shorts",
      slug: "shorts"
    },

    {
      label: "Reels",
      slug: "reels"
    },

    {
      label: "TikTok",
      slug: "tiktok"
    }
  ];

  const featuredPosts =
    blogPosts.filter(
      (post) => post.featured
    );

  const filteredPosts =
    blogPosts.filter((post) => {
      const search =
        searchQuery.toLowerCase();

      const matchesSearch =
        post.title
          .toLowerCase()
          .includes(search) ||

        post.description
          .toLowerCase()
          .includes(search) ||

        post.category
          .toLowerCase()
          .includes(search) ||

        (
          post.keywords || []
        ).some((keyword) =>
          keyword
            .toLowerCase()
            .includes(search)
        );

      const matchesCategory =
        activeCategory ===
          "all" ||
        post.category ===
          activeCategory;

      return (
        matchesSearch &&
        matchesCategory
      );
    });

  return (
    <MarketingPageShell>
      <SeoHead
        title={tt(
          "blog.seoTitle",
          "Hookora Blog | Creator Guides, Workflow Tips and SEO Content"
        )}
        description={tt(
          "blog.seoDescription",
          "Explore Hookora blog guides for creators, including hooks, captions, content workflow tips, branded Hookora articles, and Nigeria-focused creator discovery content."
        )}
        path="/blog"
      />

      <StructuredData
        id="blog-org-schema"
        data={buildOrganizationSchema()}
      />

      <StructuredData
        id="blog-page-schema"
        data={buildWebPageSchema({
          name:
            "Hookora Blog",
          description:
            "Creator guides, workflow tips, viral hook strategies, retention systems, AI creator tools, and content workflow resources.",
          url:
            "https://hookora.app/blog",
          type:
            "CollectionPage"
        })}
      />

      {/* HERO */}

      <section
        style={{
          padding:
            "84px 20px 34px",
          background:
            colors.heroBg,
          overflowX:
            "hidden"
        }}
      >
        <div
          style={{
            maxWidth:
              "1200px",
            margin: "0 auto",
            textAlign:
              "center",
            width:
              "100%",
            boxSizing:
              "border-box"
          }}
        >
          <div
            style={{
              display:
                "inline-flex",
              alignItems:
                "center",
              gap: "8px",
              padding:
                "8px 14px",
              borderRadius:
                "999px",
              background:
                colors.badgeBg,
              color:
                colors.badgeText,
              fontWeight:
                "700",
              fontSize:
                "12px",
              marginBottom:
                "18px",
              border: isDark
                ? `1px solid ${colors.border}`
                : "none"
            }}
          >
            {tt(
              "blog.title",
              "Hookora Blog"
            )}
          </div>

          <h1
            style={{
              fontSize:
                "clamp(2.4rem, 5vw, 4.2rem)",
              lineHeight:
                "1.04",
              margin:
                "0 0 16px",
              color:
                colors.text,
              letterSpacing:
                "-0.03em"
            }}
          >
            {tt(
              "blog.heroTitle",
              "Creator guides, workflow tips, and discovery content"
            )}
          </h1>

          <p
            style={{
              maxWidth:
                "780px",
              margin:
                "0 auto 24px",
              fontSize:
                "17px",
              lineHeight:
                "1.75",
              color:
                colors.textSoft
            }}
          >
            {tt(
              "blog.heroDescription",
              "Explore practical content guides built to help creators write better hooks, captions, titles, and packaging with a smoother workflow."
            )}
          </p>

          {/* SEARCH */}

          <div
            style={{
              width:
                "100%",
              maxWidth:
                "760px",
              margin:
                "0 auto 26px",
              padding:
                "0 4px",
              boxSizing:
                "border-box"
            }}
          >
            <input
              type="text"
              placeholder="Search blog posts..."
              value={
                searchQuery
              }
              onChange={(e) =>
                setSearchQuery(
                  e.target.value
                )
              }
              style={{
                width:
                  "100%",
                maxWidth:
                  "100%",
                boxSizing:
                  "border-box",
                padding:
                  "16px 18px",
                borderRadius:
                  "16px",
                border: `1px solid ${colors.borderStrong}`,
                background:
                  colors.panelBg,
                color:
                  colors.text,
                fontSize:
                  "15px",
                outline:
                  "none",
                appearance:
                  "none",
                WebkitAppearance:
                  "none"
              }}
            />
          </div>

          {/* CATEGORY FILTERS */}

          <div
            style={{
              display:
                "flex",
              justifyContent:
                "center",
              flexWrap:
                "wrap",
              gap: "12px",
              width:
                "100%",
              overflowX:
                "hidden"
            }}
          >
            {categories.map(
              (
                category
              ) => {
                const active =
                  activeCategory ===
                  category.slug;

                return (
                  <button
                    key={
                      category.slug
                    }
                    onClick={() =>
                      setActiveCategory(
                        category.slug
                      )
                    }
                    style={{
                      border:
                        active
                          ? "none"
                          : `1px solid ${colors.borderStrong}`,

                      background:
                        active
                          ? "linear-gradient(135deg, #2563eb, #7c3aed)"
                          : colors.panelBg,

                      color:
                        active
                          ? "#fff"
                          : colors.text,

                      padding:
                        "12px 16px",

                      borderRadius:
                        "999px",

                      cursor:
                        "pointer",

                      fontWeight:
                        "700",

                      fontSize:
                        "13px",

                      transition:
                        "all 0.2s ease"
                    }}
                  >
                    {
                      category.label
                    }
                  </button>
                );
              }
            )}
          </div>
        </div>
      </section>

      {/* FEATURED POSTS */}

      {activeCategory ===
        "all" &&
        searchQuery ===
          "" && (
          <section
            style={{
              padding:
                "10px 20px 30px",
              background:
                colors.sectionBg
            }}
          >
            <div
              style={{
                maxWidth:
                  "1200px",
                margin:
                  "0 auto"
              }}
            >
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  marginBottom:
                    "24px",
                  flexWrap:
                    "wrap",
                  gap: "12px"
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize:
                      "28px",
                    color:
                      colors.text
                  }}
                >
                  Featured Guides
                </h2>

                <span
                  style={{
                    color:
                      colors.textMuted,
                    fontSize:
                      "14px",
                    fontWeight:
                      "600"
                  }}
                >
                  Top creator
                  resources
                </span>
              </div>

              <div
                style={{
                  display:
                    "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(320px, 1fr))",
                  gap: "18px"
                }}
              >
                {featuredPosts
                  .slice(0, 6)
                  .map((post) => (
                    <article
                      key={
                        post.slug
                      }
                      style={{
                        background:
                          colors.cardBg,
                        border: `1px solid ${colors.border}`,
                        borderRadius:
                          "28px",
                        padding:
                          "28px",
                        boxShadow:
                          colors.shadow
                      }}
                    >
                      <div
                        style={{
                          display:
                            "flex",
                          justifyContent:
                            "space-between",
                          alignItems:
                            "center",
                          gap: "10px",
                          flexWrap:
                            "wrap",
                          marginBottom:
                            "18px"
                        }}
                      >
                        <div
                          style={{
                            display:
                              "inline-flex",
                            padding:
                              "7px 12px",
                            borderRadius:
                              "999px",
                            background:
                              "linear-gradient(135deg, #2563eb, #7c3aed)",
                            color:
                              "#fff",
                            fontWeight:
                              "700",
                            fontSize:
                              "12px"
                          }}
                        >
                          Featured
                        </div>

                        <Link
                          to={`/blog/category/${post.category}`}
                          style={{
                            textDecoration:
                              "none",
                            color:
                              colors.textMuted,
                            fontSize:
                              "12px",
                            fontWeight:
                              "700"
                          }}
                        >
                          {
                            post.categoryLabel
                          }
                        </Link>
                      </div>

                      <h2
                        style={{
                          margin:
                            "0 0 14px",
                          color:
                            colors.text,
                          fontSize:
                            "28px",
                          lineHeight:
                            "1.2"
                        }}
                      >
                        {
                          post.title
                        }
                      </h2>

                      <p
                        style={{
                          margin:
                            "0 0 22px",
                          color:
                            colors.textMuted,
                          lineHeight:
                            "1.8",
                          fontSize:
                            "15px"
                        }}
                      >
                        {
                          post.description
                        }
                      </p>

                      <div
                        style={{
                          display:
                            "flex",
                          alignItems:
                            "center",
                          justifyContent:
                            "space-between",
                          gap: "12px",
                          flexWrap:
                            "wrap"
                        }}
                      >
                        <div
                          style={{
                            display:
                              "flex",
                            gap: "10px",
                            alignItems:
                              "center",
                            color:
                              colors.textMuted,
                            fontSize:
                              "13px"
                          }}
                        >
                          <span>
                            {
                              post.readTime
                            }
                          </span>

                          <span>
                            •
                          </span>

                          <span>
                            July 2026
                          </span>
                        </div>

                        <Link
                          to={
                            post.slug
                          }
                          style={{
                            display:
                              "inline-flex",
                            textDecoration:
                              "none",
                            background:
                              colors.panelBg,
                            color:
                              colors.text,
                            padding:
                              "12px 16px",
                            borderRadius:
                              "12px",
                            fontWeight:
                              "800",
                            border: `1px solid ${colors.borderStrong}`
                          }}
                        >
                          Read Guide
                        </Link>
                      </div>
                    </article>
                  ))}
              </div>
            </div>
          </section>
        )}

      {/* POSTS */}

      <section
        style={{
          padding:
            "8px 20px 84px",
          background:
            colors.sectionBg
        }}
      >
        <div
          style={{
            maxWidth:
              "1200px",
            margin: "0 auto"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              gap: "12px",
              flexWrap:
                "wrap",
              marginBottom:
                "26px"
            }}
          >
            <div
              style={{
                color:
                  colors.textMuted,
                fontSize:
                  "14px",
                fontWeight:
                  "600"
              }}
            >
              {
                filteredPosts.length
              }{" "}
              articles found
            </div>

            {activeCategory !==
              "all" && (
              <Link
                to={`/blog/category/${activeCategory}`}
                style={{
                  textDecoration:
                    "none",
                  color:
                    "#7c3aed",
                  fontWeight:
                    "700",
                  fontSize:
                    "14px"
                }}
              >
                View category →
              </Link>
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "18px"
            }}
          >
            {filteredPosts.map(
              (post) => (
                <article
                  key={
                    post.slug
                  }
                  style={{
                    background:
                      colors.cardBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius:
                      "24px",
                    padding:
                      "24px",
                    boxShadow:
                      colors.shadow,
                    backdropFilter:
                      isDark
                        ? "blur(14px)"
                        : "none"
                  }}
                >
                  <div
                    style={{
                      display:
                        "flex",
                      justifyContent:
                        "space-between",
                      alignItems:
                        "center",
                      gap: "10px",
                      flexWrap:
                        "wrap",
                      marginBottom:
                        "14px"
                    }}
                  >
                    <div
                      style={{
                        display:
                          "inline-flex",
                        padding:
                          "7px 12px",
                        borderRadius:
                          "999px",
                        background:
                          isDark
                            ? "rgba(109,40,217,0.18)"
                            : "#f5f3ff",
                        color:
                          isDark
                            ? "#d8b4fe"
                            : "#6d28d9",
                        fontWeight:
                          "700",
                        fontSize:
                          "12px",
                        border:
                          isDark
                            ? `1px solid ${colors.border}`
                            : "none"
                      }}
                    >
                      {
                        post.badge
                      }
                    </div>

                    <Link
                      to={`/blog/category/${post.category}`}
                      style={{
                        textDecoration:
                          "none",
                        fontSize:
                          "12px",
                        fontWeight:
                          "700",
                        color:
                          colors.textMuted
                      }}
                    >
                      {
                        post.categoryLabel
                      }
                    </Link>
                  </div>

                  <h2
                    style={{
                      margin:
                        "0 0 12px",
                      color:
                        colors.text,
                      fontSize:
                        "24px",
                      lineHeight:
                        "1.25"
                    }}
                  >
                    {
                      post.title
                    }
                  </h2>

                  <p
                    style={{
                      margin:
                        "0 0 20px",
                      color:
                        colors.textMuted,
                      fontSize:
                        "15px",
                      lineHeight:
                        "1.75"
                    }}
                  >
                    {
                      post.description
                    }
                  </p>

                  {/* META */}

                  <div
                    style={{
                      display:
                        "flex",
                      gap: "12px",
                      flexWrap:
                        "wrap",
                      alignItems:
                        "center",
                      marginBottom:
                        "18px",
                      color:
                        colors.textMuted,
                      fontSize:
                        "12px",
                      fontWeight:
                        "600"
                    }}
                  >
                    <span>
                      {
                        post.readTime
                      }
                    </span>

                    <span>
                      •
                    </span>

                    <span>
                      July 2026
                    </span>
                  </div>

                  {/* KEYWORDS */}

                  <div
                    style={{
                      display:
                        "flex",
                      flexWrap:
                        "wrap",
                      gap: "8px",
                      marginBottom:
                        "22px"
                    }}
                  >
                    {(
                      post.keywords ||
                      []
                    )
                      .slice(
                        0,
                        3
                      )
                      .map(
                        (
                          keyword
                        ) => (
                          <span
                            key={
                              keyword
                            }
                            style={{
                              padding:
                                "6px 10px",
                              borderRadius:
                                "999px",
                              fontSize:
                                "11px",
                              fontWeight:
                                "600",
                              background:
                                colors.panelBg,
                              color:
                                colors.textMuted,
                              border: `1px solid ${colors.border}`
                            }}
                          >
                            {
                              keyword
                            }
                          </span>
                        )
                      )}
                  </div>

                  <Link
                    to={
                      post.slug
                    }
                    style={{
                      display:
                        "inline-flex",
                      textDecoration:
                        "none",
                      background:
                        colors.panelBg,
                      color:
                        colors.text,
                      padding:
                        "12px 16px",
                      borderRadius:
                        "12px",
                      fontWeight:
                        "800",
                      border: `1px solid ${colors.borderStrong}`
                    }}
                  >
                    {tt(
                      "common.readGuide",
                      "Read Guide"
                    )}
                  </Link>
                </article>
              )
            )}
          </div>

          {filteredPosts.length ===
            0 && (
            <div
              style={{
                padding:
                  "80px 20px",
                textAlign:
                  "center"
              }}
            >
              <h3
                style={{
                  color:
                    colors.text,
                  marginBottom:
                    "12px"
                }}
              >
                No articles found
              </h3>

              <p
                style={{
                  color:
                    colors.textMuted,
                  maxWidth:
                    "520px",
                  margin:
                    "0 auto",
                  lineHeight:
                    "1.8"
                }}
              >
                Try changing
                your search
                keywords or
                selecting a
                different
                category.
              </p>
            </div>
          )}
        </div>
      </section>
    </MarketingPageShell>
  );
}

export default BlogPage;